import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useApp } from "../../contexts/AppContext";
import { entityApi } from "../../services/entityApi";
import { raidApi } from "../../services/supabaseApi";
import { RaidEntity } from "../../types";
import { entityStats } from "../../utils/entityStats";
import { entityCategory } from "../../utils/raidAuthoring";
import { validateCombatRulePack } from "../../lib/combatRules";
import "./ResourceTables.css";
const { Title, Text } = Typography;
const editableFields = [
  "entity_name",
  "description",
  "difficulty",
  "max_hp",
  "phase_count",
  "size_tiles",
  "spawn_limit",
  "lifetime_s",
  "spawn_raid_code",
  "spawn_room_code",
  "spawn_order",
  "parent_code",
  "game_version",
] as const;
const valueText = (value: unknown) =>
  value == null ? "미입력" : String(value);

const BossBasicInfo: React.FC<{
  bossCode?: string;
  entityMode?: boolean;
  onUpdate?: () => void;
}> = ({ bossCode, entityMode, onUpdate }) => {
  const { state } = useApp();
  const code =
    bossCode ||
    (entityMode
      ? state.selectedEntity?.entity_code
      : state.selectedBoss?.boss_code) ||
    state.selectedEntity?.entity_code;
  const [data, setData] = useState<RaidEntity | null>(null);
  const [references, setReferences] = useState<{
    raids: any[];
    rooms: any[];
    entities: RaidEntity[];
  }>({ raids: [], rooms: [], entities: [] });
  const [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [editing, setEditing] = useState(false),
    [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [notice, holder] = message.useMessage();
  const request = useRef(0);
  useEffect(() => {
    const id = ++request.current;
    setEditing(false);
    setData(null);
    setError("");
    if (!code) return;
    setLoading(true);
    Promise.all([
      entityApi.getByCode(code),
      raidApi.getAll(),
      raidApi.getBossRooms(),
      entityApi.getAll(),
    ])
      .then(([entity, raids, rooms, entities]) => {
        if (id !== request.current) return;
        setData(entity);
        setReferences({ raids, rooms, entities });
      })
      .catch(() => {
        if (id === request.current)
          setError(
            "기본 정보를 불러오지 못했습니다. 새로고침 후 다시 확인해주세요.",
          );
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
    return () => {
      request.current++;
    };
  }, [code]);
  const save = async () => {
    if (!data) return;
    const savedRequest = request.current;
    try {
      const values = await form.validateFields();
      const patch: Partial<RaidEntity> = {};
      editableFields.forEach((key) => {
        const next = values[key] ?? null;
        if (next !== (data[key] ?? null)) (patch as any)[key] = next;
      });
      setSaving(true);
      if (Object.keys(patch).length) {
        const updated = await entityApi.update(data.entity_code, patch);
        if (savedRequest === request.current) setData(updated);
        window.dispatchEvent(new Event("raidDataUpdated"));
        onUpdate?.();
      }
      if (savedRequest === request.current) setEditing(false);
      notice.success("기본 정보를 저장했습니다.");
    } catch (e: any) {
      if (!e.errorFields)
        notice.error("저장하지 못했습니다. 입력 내용은 유지됩니다.");
    } finally {
      setSaving(false);
    }
  };
  const lookup = (rows: any[], key: string, name: string, value?: string) =>
    rows.find((r) => r[key] === value)?.[name] || value || "미지정";
  const selectOptions = (rows: any[], key: string, name: string) =>
    rows.map((r) => ({ value: r[key], label: r[name] }));
  if (loading)
    return (
      <div className="resource-page">
        <Spin />
        <Text> 기본 정보를 불러오는 중입니다.</Text>
      </div>
    );
  if (error)
    return (
      <div className="resource-page">
        <Alert type="error" message={error} />
      </div>
    );
  if (!data)
    return (
      <div className="resource-page">
        <Empty description="몬스터를 선택해주세요" />
      </div>
    );
  const stats = entityStats.filter((s) => (data as any)[s.key] != null);
  const combatRuleErrors = data.combat_rules ? validateCombatRulePack(data.combat_rules) : [];
  return (
    <div className="resource-page">
      {holder}
      <div className="resource-toolbar">
        <div>
          <Title level={4}>{data.entity_name}</Title>
          <Space>
            <Tag>{entityCategory(data)}</Tag>
            <Text type="secondary">기본 정보와 출현 위치를 확인하세요.</Text>
          </Space>
        </div>
        <Button
          type="primary"
          onClick={() => {
            form.resetFields();
            form.setFieldsValue(data);
            setEditing(true);
          }}
        >
          기본 정보 편집
        </Button>
      </div>
      <div className="resource-summary-grid">
        <Card size="small">
          <Text type="secondary">최대 HP</Text>
          <Title level={3}>
            {data.max_hp == null ? "미입력" : data.max_hp.toLocaleString()}
          </Title>
        </Card>
        <Card size="small">
          <Text type="secondary">페이즈</Text>
          <Title level={3}>{valueText(data.phase_count)}</Title>
        </Card>
        <Card size="small">
          <Text type="secondary">난이도</Text>
          <Title level={3}>{data.difficulty || "미지정"}</Title>
        </Card>
      </div>
      <Card size="small" title="출현 위치와 설명" style={{ marginBottom: 16 }}>
        <Descriptions
          column={{ xs: 1, sm: 2 }}
          items={[
            {
              key: "raid",
              label: "레이드",
              children: lookup(
                references.raids,
                "raid_code",
                "raid_name",
                data.spawn_raid_code,
              ),
            },
            {
              key: "room",
              label: "룸",
              children: lookup(
                references.rooms,
                "room_code",
                "room_name",
                data.spawn_room_code,
              ),
            },
            {
              key: "parent",
              label: "관련 보스",
              children: lookup(
                references.entities,
                "entity_code",
                "entity_name",
                data.parent_code,
              ),
            },
            {
              key: "order",
              label: "출현 순서",
              children: valueText(data.spawn_order),
            },
            {
              key: "desc",
              label: "설명",
              span: 2,
              children: data.description || "등록된 설명이 없습니다.",
            },
          ]}
        />
      </Card>
      <Collapse
        items={[
          {
            key: "stats",
            label: `능력치 · ${stats.length}개 입력됨`,
            children: (
              <>
                <Text type="secondary">
                  능력치 수정과 몬스터 간 비교는 레이드 관리에서 할 수 있습니다.
                  미입력과 0은 구분합니다.
                </Text>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="key"
                  dataSource={stats}
                  columns={[
                    { title: "능력치", dataIndex: "name" },
                    {
                      title: "값",
                      render: (_, s) =>
                        Number((data as any)[s.key]).toLocaleString(),
                    },
                  ]}
                  locale={{ emptyText: "입력된 능력치가 없습니다." }}
                />
              </>
            ),
          },
          {
            key: "extra",
            label: "추가 설정 · 크기와 소환 제한",
            children: (
              <Descriptions
                column={{ xs: 1, sm: 2 }}
                items={[
                  {
                    key: "size",
                    label: "크기 (타일)",
                    children: valueText(data.size_tiles),
                  },
                  {
                    key: "limit",
                    label: "생성 제한",
                    children:
                      data.spawn_limit == null
                        ? "미설정"
                        : valueText(data.spawn_limit),
                  },
                  {
                    key: "life",
                    label: "지속시간 (초)",
                    children:
                      data.lifetime_s == null
                        ? "미설정"
                        : valueText(data.lifetime_s),
                  },
                ]}
              />
            ),
          },
          {
            key: "combat-rules",
            label: `게임용 특수 규칙 (${Array.isArray(data.combat_rules?.rules) ? data.combat_rules!.rules.length : 0})`,
            children: combatRuleErrors.length ? <Alert type="error" message="특수 규칙 형식 확인이 필요합니다. 원본은 ZIP에 그대로 포함됩니다." description={combatRuleErrors.join('; ')} /> : data.combat_rules ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert type="info" showIcon message="기존 설명과 트리거를 보존한 게임 연동 데이터입니다."
                  description="DB ZIP 다운로드의 game 폴더에서 JSON과 검증 결과를 받으세요. 실제 전투 적용에는 게임 측 규칙 해석기 연결이 필요합니다." />
                <Table size="small" rowKey="id" pagination={false} dataSource={data.combat_rules.rules}
                  columns={[{ title: '특수 규칙', dataIndex: 'label' }, { title: '적용 대상', dataIndex: 'subject' }]}
                  expandable={{ expandedRowRender: rule => <pre style={{ maxHeight: 280, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{JSON.stringify(rule, null, 2)}</pre> }} />
                {data.combat_rules.unresolved.map(item => <Alert key={item.id} type="warning" message={item.detail} />)}
              </Space>
            ) : <Text type="secondary">이 몬스터에 등록된 구조화 규칙이 없습니다.</Text>,
          },
          {
            key: "meta",
            label: "코드와 관리 정보",
            children: (
              <Descriptions
                column={1}
                items={[
                  {
                    key: "code",
                    label: "몬스터 코드",
                    children: (
                      <Text code copyable>
                        {data.entity_code}
                      </Text>
                    ),
                  },
                  {
                    key: "type",
                    label: "세부 유형",
                    children: data.entity_type,
                  },
                  {
                    key: "version",
                    label: "게임 버전",
                    children: data.game_version || "미입력",
                  },
                  {
                    key: "created",
                    label: "생성일",
                    children: data.created_at || "미입력",
                  },
                  {
                    key: "updated",
                    label: "수정일",
                    children: data.updated_at || "미입력",
                  },
                ]}
              />
            ),
          },
        ]}
      />
      <Modal
        title="기본 정보 편집"
        open={editing}
        forceRender
        onCancel={() => {
          if (!saving) setEditing(false);
        }}
        onOk={save}
        confirmLoading={saving}
        okText="저장"
        cancelText="취소"
        width={720}
        closable={!saving}
        maskClosable={!saving}
        cancelButtonProps={{ disabled: saving }}
      >
        <Form form={form} layout="vertical" disabled={saving}>
          <Form.Item
            name="entity_name"
            label="이름"
            rules={[{ required: true, message: "이름을 입력해주세요." }]}
          >
            <Input />
          </Form.Item>
          <div className="resource-summary-grid">
            <Form.Item name="max_hp" label="최대 HP">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="phase_count" label="페이즈 수">
              <InputNumber min={1} precision={0} />
            </Form.Item>
            <Form.Item name="difficulty" label="난이도">
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="description" label="설명">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Collapse
            key={String(editing)}
            items={[
              {
                key: "location",
                label: "출현 위치 설정",
                forceRender: true,
                children: (
                  <>
                    <Form.Item name="spawn_raid_code" label="레이드">
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={selectOptions(
                          references.raids,
                          "raid_code",
                          "raid_name",
                        )}
                      />
                    </Form.Item>
                    <Form.Item name="spawn_room_code" label="룸">
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={selectOptions(
                          references.rooms,
                          "room_code",
                          "room_name",
                        )}
                      />
                    </Form.Item>
                    <Form.Item name="parent_code" label="관련 보스">
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={selectOptions(
                          references.entities.filter(
                            (e) =>
                              e.entity_code !== data.entity_code &&
                              e.entity_type.startsWith("BOSS"),
                          ),
                          "entity_code",
                          "entity_name",
                        )}
                      />
                    </Form.Item>
                    <Form.Item name="spawn_order" label="출현 순서">
                      <InputNumber min={1} precision={0} />
                    </Form.Item>
                  </>
                ),
              },
              {
                key: "advanced",
                label: "추가 설정",
                forceRender: true,
                children: (
                  <>
                    <Form.Item name="size_tiles" label="크기 (타일)">
                      <InputNumber min={1} />
                    </Form.Item>
                    <Form.Item name="spawn_limit" label="생성 제한">
                      <InputNumber min={0} />
                    </Form.Item>
                    <Form.Item name="lifetime_s" label="지속시간 (초)">
                      <InputNumber min={0} />
                    </Form.Item>
                    <Form.Item name="game_version" label="게임 버전">
                      <Input />
                    </Form.Item>
                  </>
                ),
              },
            ]}
          />
        </Form>
      </Modal>
    </div>
  );
};
export default BossBasicInfo;
